'use client';
import { useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useSWRList } from '@hooks/use-swr-list';
import { BaseService, useAuthenticatedSWR, useService } from '../api';
import { useSessionCustomer } from './../customer';
import { ORDERS_ENDPOINT, getOrderHistoryPath } from './constants';

class OrderService extends BaseService {
  get = orderId => this.client.get(`${ORDERS_ENDPOINT}${orderId}/`);
  list = () => this.client.get(ORDERS_ENDPOINT);
}

export const useOrders = () => {
  const { data, error } = useAuthenticatedSWR(ORDERS_ENDPOINT);
  return {
    orders: data?.data,
    error,
    isLoading: !data && !error,
  };
};

export const useOrder = orderId => {
  const { data, error } = useAuthenticatedSWR(
    orderId ? `${ORDERS_ENDPOINT}${orderId}/` : null,
  );

  return {
    order: data,
    isLoading: orderId ? !error && !data : false,
    error,
  };
};

export const useOrderList = orderIds => {
  const service = useService(OrderService);
  const { mutate: globalMutate } = useSWRConfig();
  const { data, error } = useSWRList(
    orderIds?.map(id => `${ORDERS_ENDPOINT}${id}/`),
  );

  const listOrders = async () => {
    const response = await service.list();
    const ordersIds = response.data.data.map(order => order.id);
    const ordersResponses = await Promise.all(
      ordersIds.map(id => service.get(id)),
    );
    const orders = ordersResponses.map(response => response.data);
    globalMutate(ORDERS_ENDPOINT, response.data, false);

    for (const order of orders) {
      if (order.id) {
        globalMutate(`${ORDERS_ENDPOINT}${order.id}/`, order, false);
      }
    }

    return orders;
  };

  return {
    orders: data,
    isLoading: !error && !data,
    listOrders,
    error,
  };
};

export const useOrderHistory = (limit = 5) => {
  const { customer, isLoading: isCustomerLoading } = useSessionCustomer();
  const getKey = (pageIndex, previousPageData) => {
    pageIndex = pageIndex + 1;
    if (
      isCustomerLoading ||
      (previousPageData && !previousPageData?.data.length)
    )
      return null; // reached the end
    return getOrderHistoryPath(customer?.id, pageIndex, limit); // SWR key
  };
  const {
    data,
    size,
    setSize,
    isLoading: isOrdersLoading,
    isValidating,
    error,
  } = useSWRInfinite(getKey);
  const orders = data?.flatMap(resp => resp.data);
  const nextPage = data?.length ? data[data.length - 1].next_page : null;
  const orderCount = data?.length ? data[0].count : 0;
  return {
    orderCount,
    nextPage,
    orders,
    size,
    setSize,
    isLoading: isOrdersLoading || isCustomerLoading,
    isValidating,
    error,
  };
};
